import { useQueryClient } from "@tanstack/react-query";
import { Copy, Languages, Rocket, Sparkles } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type * as S from "@shipyard/shared";
import { NOTES_MAX_LENGTH } from "@shipyard/shared/constants";
import { http } from "@/lib/api";
import { useReleaseCandidates } from "@/lib/queries";
import { cn, formatDate } from "@/lib/utils";
import { CommitPicker } from "./commit-picker";
import { PlatformIcon, toastError } from "./common";
import { TranslateDialog } from "./translate-dialog";
import { Button, Dialog, ErrorBox, Field, Input, Select, Spinner, Textarea } from "./ui";

const MAX = Math.min(NOTES_MAX_LENGTH.ios.store, NOTES_MAX_LENGTH.android.store);

function storeTexts(build: S.Build | undefined): Record<string, string> {
  return Object.fromEntries((build?.notes ?? []).filter((n) => n.target === "store").map((n) => [n.locale, n.text]));
}

function StepTitle({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold">
      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-600 text-xs text-white">{n}</span>
      {children}
    </h3>
  );
}

/** Promote an iOS and an Android build together, with release notes written once for both stores. */
export function JointReleaseDialog({ app, open, onOpenChange }: { app: S.App; open: boolean; onOpenChange: (o: boolean) => void }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const candidates = useReleaseCandidates(app.id, open);
  const [iosId, setIosId] = useState("");
  const [androidId, setAndroidId] = useState("");
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [head, setHead] = useState("");
  const [releaseMode, setReleaseMode] = useState(app.iosReleaseMode);
  const [track, setTrack] = useState<"production" | "beta" | "alpha">("production");
  const [rollout, setRollout] = useState(Math.round(app.androidRollout * 100));
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [translateOpen, setTranslateOpen] = useState(false);

  const ios = candidates.data?.ios.find((b) => b.id === iosId);
  const android = candidates.data?.android.find((b) => b.id === androidId);
  const locales = useMemo(() => [...new Set([...app.locales, ...Object.keys(notes)])], [app.locales, notes]);
  const hasGit = !!app.gitProviderId && !!app.gitRepo;
  const hasLlm = !!app.llmProviderId;

  // Suggested pair, then prefill the notes from the iOS build (or the Android one).
  useEffect(() => {
    if (!open || !candidates.data) return;
    setIosId(candidates.data.suggested.iosBuildId ?? "");
    setAndroidId(candidates.data.suggested.androidBuildId ?? "");
    setError(null);
  }, [open, candidates.data]);
  useEffect(() => {
    const fromIos = storeTexts(ios);
    setNotes(Object.keys(fromIos).length ? fromIos : storeTexts(android));
    setHead(ios?.commitSha ?? android?.commitSha ?? "");
  }, [ios, android]);

  const preview = async (body: Record<string, unknown>) => {
    const res = await http.post<{ notes: Record<string, string>; commitCount: number }>(`/apps/${app.id}/releases/notes/generate`, {
      buildIds: [iosId, androidId].filter(Boolean),
      ...body,
    });
    setNotes((n) => ({ ...n, ...res.notes }));
    return res;
  };

  const generateFromCommits = async () => {
    setBusy("commits");
    setError(null);
    try {
      const res = await preview({ mode: "commits", locales, headCommit: head || undefined });
      toast.success(t("build.generated", { count: res.commitCount }));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  const copyPrevious = async () => {
    setBusy("copy");
    setError(null);
    try {
      const recent = await http.get<S.Build[]>(`/apps/${app.id}/builds?limit=100`);
      const previous = recent.find((b) => b.id !== iosId && b.id !== androidId && b.notes.some((n) => n.target === "store" && n.text.trim()));
      if (!previous) throw new Error(t("release.noPrevious"));
      setNotes(storeTexts(previous));
      toast.success(t("release.copiedFrom", { version: previous.version, build: previous.buildNumber }));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  const tooLong = locales.some((l) => (notes[l] ?? "").length > MAX);
  const filled = locales.filter((l) => notes[l]?.trim());
  const versionMismatch = ios && android && ios.version !== android.version;

  const submit = async () => {
    setBusy("submit");
    setError(null);
    try {
      await http.post(`/apps/${app.id}/releases`, {
        iosBuildId: iosId,
        androidBuildId: androidId,
        notes: filled.map((locale) => ({ locale, text: notes[locale]!.trim() })),
        ios: { releaseMode },
        android: { track, rollout: Math.min(100, Math.max(1, rollout)) / 100 },
      });
      toast.success(t("release.started"));
      void qc.invalidateQueries({ queryKey: ["apps", app.id] });
      void qc.invalidateQueries({ queryKey: ["builds"] });
      onOpenChange(false);
    } catch (err) {
      setError(err);
      toastError(err);
    } finally {
      setBusy(null);
    }
  };

  const buildOption = (b: S.Build) => `${b.version} (${b.buildNumber}) · ${t(`status.${b.status}`)} · ${formatDate(b.createdAt, i18n.language)}`;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      wide="xl"
      title={t("release.title")}
      description={t("release.description")}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button onClick={submit} loading={busy === "submit"} disabled={!iosId || !androidId || filled.length === 0 || tooLong}>
            <Rocket className="h-4 w-4" />
            {t("release.submit")}
          </Button>
        </>
      }
    >
      {candidates.isLoading ? (
        <Spinner />
      ) : (
        <div className="space-y-6">
          <section>
            <StepTitle n={1}>{t("release.stepBuilds")}</StepTitle>
            <div className="grid gap-4 md:grid-cols-2">
              <Field label={<span className="flex items-center gap-1.5"><PlatformIcon platform="ios" />iOS</span>} htmlFor="jr-ios">
                <Select id="jr-ios" value={iosId} onChange={(e) => setIosId(e.target.value)}>
                  {!candidates.data?.ios.length && <option value="">{t("release.noCandidate")}</option>}
                  {candidates.data?.ios.map((b) => (
                    <option key={b.id} value={b.id}>
                      {buildOption(b)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={<span className="flex items-center gap-1.5"><PlatformIcon platform="android" />Android</span>} htmlFor="jr-android">
                <Select id="jr-android" value={androidId} onChange={(e) => setAndroidId(e.target.value)}>
                  {!candidates.data?.android.length && <option value="">{t("release.noCandidate")}</option>}
                  {candidates.data?.android.map((b) => (
                    <option key={b.id} value={b.id}>
                      {buildOption(b)} · {b.track}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            {versionMismatch && <p className="mt-2 text-xs text-amber-600">{t("release.versionMismatch", { ios: ios!.version, android: android!.version })}</p>}
            <p className="mt-2 text-xs text-fg-muted">{t("release.candidatesHelp")}</p>
          </section>

          <section>
            <StepTitle n={2}>{t("release.stepNotes")}</StepTitle>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" onClick={generateFromCommits} loading={busy === "commits"} disabled={!hasLlm || !hasGit || !iosId}>
                <Sparkles className="h-3.5 w-3.5" />
                {t("build.fillCommits")}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setTranslateOpen(true)} disabled={!hasLlm || filled.length === 0}>
                <Languages className="h-3.5 w-3.5" />
                {t("build.fillTranslate")}
              </Button>
              <Button size="sm" variant="outline" onClick={copyPrevious} loading={busy === "copy"}>
                <Copy className="h-3.5 w-3.5" />
                {t("build.fillCopy")}
              </Button>
              {(!hasLlm || !hasGit) && <span className="text-xs text-fg-muted">{!hasLlm ? t("build.fillNeedsLlm") : t("build.fillNeedsLlmGit")}</span>}
            </div>
            {hasGit && hasLlm && (
              <Field label={t("build.headCommit")} help={t("release.commitHelp")} htmlFor="jr-sha" className="mb-4">
                <CommitPicker appId={app.id} hasGit value={head} onChange={setHead} autoSelectLatest={!head} idPrefix="jr" />
              </Field>
            )}
            <div className="space-y-3">
              {locales.map((locale) => {
                const value = notes[locale] ?? "";
                return (
                  <div key={locale} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <label htmlFor={`jr-${locale}`} className="font-medium">
                        {locale}
                      </label>
                      <span className={cn("text-xs", value.length > MAX ? "text-red-600" : "text-fg-muted")}>{t("build.chars", { count: value.length, max: MAX })}</span>
                    </div>
                    <Textarea id={`jr-${locale}`} rows={3} value={value} onChange={(e) => setNotes((n) => ({ ...n, [locale]: e.target.value }))} />
                  </div>
                );
              })}
            </div>
            <p className="mt-2 text-xs text-fg-muted">{t("release.notesHelp", { max: MAX })}</p>
          </section>

          <section>
            <StepTitle n={3}>{t("release.stepOptions")}</StepTitle>
            <div className="grid gap-4 md:grid-cols-3">
              <Field label={<span className="flex items-center gap-1.5"><PlatformIcon platform="ios" />{t("build.releaseMode")}</span>} htmlFor="jr-mode">
                <Select id="jr-mode" value={releaseMode} onChange={(e) => setReleaseMode(e.target.value as S.IosReleaseMode)}>
                  <option value="manual">{t("build.releaseModeManual")}</option>
                  <option value="after_approval">{t("build.releaseModeAuto")}</option>
                </Select>
              </Field>
              <Field label={<span className="flex items-center gap-1.5"><PlatformIcon platform="android" />{t("build.targetTrack")}</span>} htmlFor="jr-track">
                <Select id="jr-track" value={track} onChange={(e) => setTrack(e.target.value as typeof track)}>
                  <option value="production">production</option>
                  <option value="beta">beta</option>
                  <option value="alpha">alpha</option>
                </Select>
              </Field>
              <Field label={t("build.rollout")} htmlFor="jr-rollout">
                <Input id="jr-rollout" type="number" min={1} max={100} value={rollout} onChange={(e) => setRollout(Number(e.target.value))} />
              </Field>
            </div>
          </section>
          <ErrorBox error={error} />
        </div>
      )}
      <TranslateDialog
        open={translateOpen}
        onOpenChange={setTranslateOpen}
        locales={locales}
        texts={notes}
        onTranslate={async (body) => {
          await preview({ mode: "translate", ...body });
        }}
      />
    </Dialog>
  );
}
